import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAutoFitRows } from './useAutoFitRows';
import { findPart, findParts, TanstackTablePart } from '../TanstackTable/parts';
import { TanstackTableLocale, useTanstackTableLocale } from '../TanstackTable/locale';
import { PageSizeOption } from '../TanstackTable/types';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../TanstackTable/constants';

export interface UseAutoPageSizeOptions {
  /** The plain sizes offered alongside the fitted one. */
  pageSizeOptions?: PageSizeOption[];
  /** Smallest page to shrink to. Default `5`. */
  minRows?: number;
  /** Largest page to grow to. Default `100`. */
  maxRows?: number;
  /** Space left below the table. Default `16`. */
  bottomGap?: number;
  /**
   * Round the fitted size down to a multiple of this. Default `1` — an exact
   * fit, right when re-paginating is free. Raise it when each distinct size
   * costs something, as `useServerPagination` does.
   */
  step?: number;
  /** Overrides the `Auto (n)` label; defaults to the ambient table locale. */
  localeText?: Partial<TanstackTableLocale>;
  /**
   * Stop choosing a page size: no `Auto (n)` entry and `pageSize` stays `null`.
   * Measuring continues regardless — the scroll-area cap is what keeps the
   * header and footer on screen, and that is wanted whether or not the page
   * size is being fitted. Default `false`.
   */
  disabled?: boolean;
}

/** The props `useAutoPageSize` contributes to a table. */
export interface AutoPageSizeTableProps {
  rootRef: RefObject<HTMLDivElement>;
  pageSizeOptions: PageSizeOption[];
  onPageSizePicked: (pageSize: number) => void;
  renderSizingRow: boolean;
  maxScrollAreaHeight: number | undefined;
}

export interface UseAutoPageSizeResult {
  /** The fitted row count, or `null` while the user has pinned a size. */
  pageSize: number | null;
  /** Spread onto the table. */
  tableProps: AutoPageSizeTableProps;
}

/** Only used until a row exists to measure; the sizing row makes this rare. */
const COLD_START_ROW_HEIGHT = 40;

/** Parts of the table that occupy vertical space rows cannot use. */
const CHROME_PARTS: TanstackTablePart[] = ['toolbar', 'head', 'pagination'];

/**
 * Fits a `TanstackTable`'s page size to the space between it and the fold.
 *
 * All of the measuring lives here rather than in the table. Given the table's
 * root element it finds the toolbar, header, rows and footer by their
 * `TANSTACK_TABLE_PART` markers and hands the geometry to `useAutoFitRows`; the
 * table stays a renderer that knows nothing about any of it.
 *
 * It computes and offers a size — it never applies one. `useClientPagination`
 * and `useServerPagination` wrap this and decide when to act on it.
 */
export function useAutoPageSize({
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  minRows = 5,
  maxRows = 100,
  bottomGap = 16,
  step = 1,
  localeText,
  disabled = false,
}: UseAutoPageSizeOptions = {}): UseAutoPageSizeResult {
  const inheritedLocale = useTanstackTableLocale();
  const autoPageSizeLabel = localeText?.autoPageSizeLabel ?? inheritedLocale.autoPageSizeLabel;

  // The size the user pinned by hand, if any. Auto-fit keeps measuring while
  // pinned so the `Auto (n)` entry stays offerable — and accurate — for going
  // back to it.
  const [pinnedPageSize, setPinnedPageSize] = useState<number | null>(null);

  // The header scrolls *with* the rows, so it belongs to the scroll area's
  // height even though it is reserved out of the row count. Captured on each
  // measurement pass, and read back on the render that measurement triggers.
  const headHeightRef = useRef(0);

  // The last mean height real rows had. The sizing row is a single blank line
  // and so always shorter, which matters because a table empties on the way to
  // being refilled: every refetch would otherwise measure the sizing row,
  // inflate the fitted size, and shove the reader to a different page before
  // the new rows land. Once real rows have been seen, they are the better
  // answer even when they are momentarily gone.
  const lastRealRowHeightRef = useRef<number | null>(null);

  // Whether a real row has ever been measured. Once one has, the sizing row has
  // done its job and is retired — it would otherwise keep sitting above the
  // "no rows" message for the rest of the table's life, nudging it down a line.
  const [hasMeasuredRealRow, setHasMeasuredRealRow] = useState(false);

  // `useAutoFitRows` measures from this element's top edge, and it is also the
  // table's root, so the parts below are looked up through the very same ref.
  const {
    containerRef: rootRef,
    rows: fittedPageSize,
    availableHeight,
  } = useAutoFitRows<HTMLDivElement>({
    // Chrome inside the table that rows do not get. Read fresh on each pass
    // rather than held as refs — the table owns this markup and may swap it out.
    reserve: [
      (): number =>
        CHROME_PARTS.reduce((total: number, part: TanstackTablePart) => {
          const element = findPart(rootRef.current, part);
          if (!element) return total;
          const height = element.getBoundingClientRect().height;
          if (part === 'head') headHeightRef.current = height;
          return total + height;
        }, 0),
    ],
    // Mean height of the rows on screen. A single sample is wrong as soon as
    // rows differ — a table where some cells wrap to two lines would be measured
    // off whichever row happened to be first, overflow the fold, and never
    // correct itself, because re-reading that row gives the same answer.
    rowHeight: (): number | null => {
      const rowElements = findParts(rootRef.current, 'row');
      const realRows = rowElements.filter(
        (element: HTMLElement) => !element.hasAttribute('aria-hidden')
      );

      if (realRows.length) {
        const total = realRows.reduce(
          (accumulated: number, element: HTMLElement) =>
            accumulated + element.getBoundingClientRect().height,
          0
        );
        const mean = total / realRows.length;
        if (mean > 0) lastRealRowHeightRef.current = mean;
        return mean;
      }

      // Nothing but the sizing row. Anything real we measured earlier describes
      // this table better than a blank line does; the sizing row is only the
      // right answer on a cold start, when there is nothing else.
      if (lastRealRowHeightRef.current !== null) return lastRealRowHeightRef.current;
      return rowElements.length ? rowElements[0].getBoundingClientRect().height : null;
    },
    // Rows can re-wrap — changing their mean height — while the table's own box
    // stays exactly where it is, which no observer on the root would notice.
    observe: (): (Element | null)[] => [findPart(rootRef.current, 'body')],
    fallbackRowHeight: COLD_START_ROW_HEIGHT,
    bottomGap,
    minRows,
    maxRows,
    step,
  });

  // Promoted out of the measurement above: `rowHeight` is called for its return
  // value and must stay free of side effects, so the flag is picked up here
  // instead. Flips once and then costs a boolean test per render.
  useEffect(() => {
    if (!hasMeasuredRealRow && lastRealRowHeightRef.current !== null) setHasMeasuredRealRow(true);
  });

  const isActive = !disabled && pinnedPageSize === null && fittedPageSize > 0;

  // The value the `Auto (n)` entry carries, or `null` when it is not offered.
  //
  // It is withheld in one case: the user has pinned a size and the fitted size
  // has drifted onto that same number. The select labels the current value by
  // matching it against the option values, so offering it here would relabel
  // their pinned size as `Auto (n)` and claim a fit that is not running. The
  // entry returns as soon as the two diverge.
  const autoOptionValue = useMemo<number | null>(() => {
    if (disabled || fittedPageSize <= 0) return null;
    if (pinnedPageSize !== null && pinnedPageSize === fittedPageSize) return null;
    return fittedPageSize;
  }, [disabled, fittedPageSize, pinnedPageSize]);

  // A fitted size rarely lands on a round number, and a select renders blank for
  // a value absent from its options. Where the auto entry is offered it
  // *replaces* the plain option of the same value — two entries sharing a value
  // are indistinguishable in the menu while meaning different things.
  const effectivePageSizeOptions = useMemo<PageSizeOption[]>(() => {
    if (autoOptionValue === null) return pageSizeOptions;

    const autoOption = {
      value: autoOptionValue,
      label: autoPageSizeLabel.replace('{rows}', String(autoOptionValue)),
    };

    return [
      ...pageSizeOptions.filter(
        (option: PageSizeOption) =>
          (typeof option === 'number' ? option : option.value) !== autoOptionValue
      ),
      autoOption,
    ].sort(
      (a: PageSizeOption, b: PageSizeOption) =>
        (typeof a === 'number' ? a : a.value) - (typeof b === 'number' ? b : b.value)
    );
  }, [autoOptionValue, pageSizeOptions, autoPageSizeLabel]);

  // Only a pick of the entry actually offered as `Auto (n)` resumes auto-fit;
  // every other pick — including the plain option of the same value when the
  // auto entry is withheld — pins the size.
  const handlePageSizePicked = useCallback(
    (pageSize: number): void =>
      setPinnedPageSize(autoOptionValue !== null && pageSize === autoOptionValue ? null : pageSize),
    [autoOptionValue]
  );

  // Exactly the space between the table and the fold, so the table as a whole
  // always fits: header and footer stay put and only the rows scroll. A pinned
  // page size larger than the fit spills into this scroll area instead of
  // pushing the page past the fold and taking the chrome with it.
  const maxScrollAreaHeight =
    availableHeight > 0 ? availableHeight + headHeightRef.current : undefined;

  const tableProps = useMemo<AutoPageSizeTableProps>(
    () => ({
      rootRef,
      pageSizeOptions: effectivePageSizeOptions,
      onPageSizePicked: handlePageSizePicked,
      renderSizingRow: !disabled && !hasMeasuredRealRow,
      maxScrollAreaHeight,
    }),
    [
      rootRef,
      effectivePageSizeOptions,
      handlePageSizePicked,
      maxScrollAreaHeight,
      disabled,
      hasMeasuredRealRow,
    ]
  );

  return { pageSize: isActive ? fittedPageSize : null, tableProps };
}

export default useAutoPageSize;
